import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { PlusIcon } from '@heroicons/react/24/outline'
import { Badge, Button, DataTable, FilterChips, Modal, PageHeader, type ColumnDef, type FilterChipOption } from '@lipaflow/ui'
import {
  adminService,
  type IntegrationConfig,
  type IntegrationCreatePayload,
  type IntegrationUpdatePayload,
  type TenantUserSummary,
} from '../services/admin'
import { usePageTitle } from '../lib/pageTitle'
import { useSector } from '../sector/sector'
import { apiErrorMessage } from '@lipaflow/shared'

const BANKS = [
  { value: 'kcb', label: 'KCB' },
  { value: 'coop', label: 'Co-op Bank' },
  { value: 'equity', label: 'Equity Bank' },
  { value: 'ncba', label: 'NCBA' },
  { value: 'mpesa', label: 'Safaricom M-PESA (till or paybill)' },
]
const bankLabel = (v: string) => BANKS.find((b) => b.value === v)?.label ?? v.toUpperCase()

type Status = '' | 'active' | 'testing' | 'inactive'
const STATUS: Record<string, { label: string; variant: 'success' | 'warning' | 'neutral' }> = {
  active: { label: 'Live', variant: 'success' },
  testing: { label: 'Testing', variant: 'warning' },
  inactive: { label: 'Off', variant: 'neutral' },
}

type FormState = {
  provider: string
  display_name: string
  status: string
  client_id: string
  client_secret: string
  api_key: string
  base_url: string
  token_url: string
  webhook_secret: string
  signing_key: string
  allowed_ips: string
  capabilities: string
  config: string
  /** Who the account is registered to, as the bank confirmed it. */
  setup_payment_reference: string
  account_holder_name: string
}
const emptyForm: FormState = {
  provider: 'kcb', display_name: '', status: 'testing', client_id: '', client_secret: '', api_key: '',
  base_url: '', token_url: '', webhook_secret: '', signing_key: '', allowed_ips: '', capabilities: 'stk_push,c2b', config: '{}',
  account_holder_name: '', setup_payment_reference: '',
}

/** Jenga (Equity) sandbox defaults. Switch both addresses to api.finserve.africa to go live. */
const EQUITY_SANDBOX = {
  base_url: 'https://uat.finserve.africa',
  token_url: 'https://uat.finserve.africa/authentication/api/v3/authenticate/merchant',
  capabilities: 'c2b, account_query, reference_lookup',
  config: JSON.stringify({ account_number: '', country_code: 'KE', ipn_username: '' }, null, 2),
}

/**
 * Daraja sandbox defaults for a till or paybill connected directly with
 * Safaricom. Switch both addresses to api.safaricom.co.ke to go live. The
 * callback token is generated by LipaFlow; Safaricom's addresses are allowed
 * by default.
 */
const MPESA_SANDBOX = {
  base_url: 'https://sandbox.safaricom.co.ke',
  token_url: 'https://sandbox.safaricom.co.ke/oauth/v1/generate?grant_type=client_credentials',
  capabilities: 'c2b, account_query, stk_push',
  config: JSON.stringify({
    paybill_number: '', shortcode_type: 'paybill', till_number: '', nominated_number: '',
    reject_unknown_accounts: false, verify_on_callback: true,
  }, null, 2),
}

const splitList = (input: string) => input.split(/[\n,]/g).map((s) => s.trim()).filter(Boolean)
/** The JSON settings with the bank-confirmed holder name written in. */
function configWithHolder(form: FormState): Record<string, unknown> {
  const config = parseConfig(form.config)
  const reference = form.setup_payment_reference.trim()
  if (reference) config.setup_payment_reference = reference
  else delete config.setup_payment_reference
  const holder = form.account_holder_name.trim()
  if (holder) config.account_holder_name = holder
  else delete config.account_holder_name
  return config
}

function parseConfig(input: string): Record<string, unknown> {
  const t = input.trim()
  if (!t) return {}
  const parsed = JSON.parse(t) as unknown
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('Extra settings must be a JSON object')
  return parsed as Record<string, unknown>
}
function when(value: string | null): string {
  if (!value) return 'Never'
  return new Date(value).toLocaleString('en-KE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
const field = 'w-full rounded-button border border-hairline bg-surface p-2.5 text-body-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent'
const labelCls = 'block text-caption text-ink-muted mb-1'

/**
 * Payment connections — a customer's bank connections (receive and check
 * payments). You always work on one customer at a time, acting through one
 * of their admins, so credentials never land on the wrong customer.
 */
/** A sector's customers' payment connections (banks, M-PESA). */
export default function IntegrationsPage() {
  usePageTitle('Payment connections')
  const sector = useSector()
  const queryClient = useQueryClient()
  const [customerId, setCustomerId] = useState('')
  const [adminId, setAdminId] = useState('')
  const [context, setContext] = useState<{ token: string; name: string; admin: string } | null>(null)
  const [status, setStatus] = useState<Status>('')
  const [bank, setBank] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<IntegrationConfig | null>(null)
  const [adding, setAdding] = useState(false)

  const { data: allCustomers = [] } = useQuery({
    queryKey: ['tenants', sector.code],
    queryFn: () => adminService.listTenants(sector.code),
  })
  // Only organisations that can take payments: a sample account closed when
  // its owner went live shares their name and would be listed twice. Filtered
  // here, not in the query: other pages share that cached list.
  const customers = useMemo(() => allCustomers.filter((c) => c.is_active), [allCustomers])
  // Another sector's customer is not this page's: start over when switching.
  useEffect(() => { setCustomerId(''); setContext(null) }, [sector.code])
  const { data: admins = [], isError: adminsFailed } = useQuery({
    queryKey: ['tenant-admin-users', customerId],
    queryFn: async (): Promise<TenantUserSummary[]> =>
      (await adminService.listTenantUsers(customerId)).filter((u) => u.is_active && u.is_verified && u.is_administrator),
    enabled: Boolean(customerId),
  })
  useEffect(() => {
    if (!admins.some((a) => a.id === adminId)) setAdminId(admins[0]?.id ?? '')
  }, [admins, adminId])
  useEffect(() => setPage(1), [status, bank])

  const start = useMutation({
    mutationFn: () => adminService.startSupportAccess(customerId, adminId),
    onSuccess: (s) => { setContext({ token: s.access_token, name: s.tenant_name, admin: s.target_user.full_name }); setPage(1) },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't switch to that customer")),
  })
  const token = context?.token

  const list = useQuery({
    queryKey: ['integrations', customerId, status, bank, page, token],
    queryFn: () => adminService.listIntegrations({ provider: bank || undefined, status: status || undefined, page, per_page: 20 }, token),
    enabled: Boolean(token),
  })

  const leave = () => { setContext(null); setSelected(null); setAdding(false) }

  if (!context) {
    return (
      <div className="space-y-6">
        <PageHeader title="Payment connections" subtitle="Bank connections that let a customer receive and check payments." />
        <section className="rounded-card border border-hairline bg-surface p-6 space-y-4 max-w-2xl">
          <div>
            <h2 className="font-outfit text-h4 text-ink">Choose a customer first</h2>
            <p className="text-body-sm text-ink-muted mt-1">
              You work on one customer at a time so bank credentials can't be saved to the wrong one. Changes are made
              through one of their administrators and recorded in the activity log.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className={labelCls}>Customer</span>
              <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className={field} aria-label="Customer">
                <option value="">Choose…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className={labelCls}>Act as administrator</span>
              <select
                value={adminId}
                onChange={(e) => setAdminId(e.target.value)}
                className={field}
                aria-label="Act as administrator"
                disabled={!customerId || admins.length === 0}
              >
                <option value="">{!customerId ? 'Choose a customer first' : adminsFailed ? 'Not loaded' : admins.length === 0 ? 'No verified administrator' : 'Choose…'}</option>
                {admins.map((a) => <option key={a.id} value={a.id}>{a.full_name} · {a.email}</option>)}
              </select>
              {adminsFailed && <span role="alert" className="mt-1 block text-caption text-warning">Couldn't load their administrators. Try again.</span>}
            </label>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => start.mutate()} disabled={!customerId || !adminId} isLoading={start.isPending}>
              Work on this customer
            </Button>
          </div>
        </section>
      </div>
    )
  }

  const views: FilterChipOption<Status>[] = [
    { key: '', label: 'All' },
    { key: 'active', label: 'Live' },
    { key: 'testing', label: 'Testing' },
    { key: 'inactive', label: 'Off' },
  ]
  const columns: ColumnDef<IntegrationConfig>[] = [
    {
      key: 'name',
      header: 'Connection',
      cell: (i) => (
        <div>
          <div className="font-medium text-ink">{i.display_name}</div>
          <div className="text-caption text-ink-muted">{bankLabel(i.provider)}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (i) => {
        const s = STATUS[i.status] ?? { label: i.status, variant: 'neutral' as const }
        return <Badge variant={s.variant} size="sm">{s.label}</Badge>
      },
    },
    {
      key: 'check',
      header: 'Last check',
      hideOnMobile: true,
      cell: (i) => (
        <div className="text-caption">
          <div className="text-ink">{i.last_health_status || 'Not checked yet'}</div>
          <div className="text-ink-muted">{when(i.last_health_check)}</div>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payment connections"
        subtitle="Bank connections that let this customer receive and check payments."
        actions={<Button leftIcon={<PlusIcon className="h-4 w-4" />} onClick={() => setAdding(true)}>Add a connection</Button>}
      />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-card bg-accent-soft px-4 py-3">
        <p className="text-body-sm text-ink">
          Working on <span className="font-semibold">{context.name}</span> as {context.admin}. Everything you change here applies only to this customer.
        </p>
        <Button size="sm" variant="secondary" onClick={leave}>Switch customer</Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <FilterChips options={views} value={status} onChange={setStatus} />
        <select aria-label="Bank" value={bank} onChange={(e) => setBank(e.target.value)} className="rounded-button border border-hairline bg-surface p-2 text-body-sm text-ink">
          <option value="">All banks</option>
          {BANKS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
      </div>

      <DataTable
        data={list.data?.items ?? []}
        columns={columns}
        getRowId={(i) => i.id}
        loading={list.isLoading}
        pagination={list.data ? { page, pageSize: 20, total: list.data.total } : undefined}
        onPageChange={setPage}
        onRowClick={setSelected}
        emptyState={{
          title: 'No connections yet',
          description: 'Add a bank connection, test it, then set it live.',
          action: <Button onClick={() => setAdding(true)}>Add a connection</Button>,
        }}
      />

      <ConnectionPanel integration={selected} token={token!} onClose={() => setSelected(null)} />
      <AddConnectionModal
        open={adding}
        token={token!}
        onClose={() => setAdding(false)}
        onCreated={() => queryClient.invalidateQueries({ queryKey: ['integrations'] })}
      />
    </div>
  )
}

function ConnectionPanel({ integration, token, onClose }: { integration: IntegrationConfig | null; token: string; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'view' | 'edit' | 'off'>('view')
  const [form, setForm] = useState<FormState>(emptyForm)
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['integrations'] })
  const close = () => { setMode('view'); onClose() }

  const test = useMutation({
    mutationFn: () => adminService.testIntegration(integration!.id, undefined, token),
    onSuccess: (r) => {
      if (r.success) toast.success(`Connected (${r.latency_ms ?? '–'} ms)`)
      else toast.error(r.auth_error || 'The bank refused the connection')
      refresh()
    },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't run the test")),
  })
  const save = useMutation({
    mutationFn: (payload: IntegrationUpdatePayload) => adminService.updateIntegration(integration!.id, payload, token),
    onSuccess: () => { toast.success('Saved'); refresh(); setMode('view') },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't save")),
  })
  const register = useMutation({
    mutationFn: () => adminService.registerMpesa(integration!.id, token),
    onSuccess: (r) => { toast.success(`Safaricom: ${r.c2b ?? 'registered'}`); refresh() },
    onError: (e) => toast.error(apiErrorMessage(e, "Safaricom didn't accept the registration")),
  })
  const off = useMutation({
    mutationFn: () => adminService.deactivateIntegration(integration!.id, token),
    onSuccess: () => { toast.success('Connection turned off'); refresh(); close() },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't turn it off")),
  })

  if (!integration) return <Modal open={false} onClose={close}>{null}</Modal>
  const s = STATUS[integration.status] ?? { label: integration.status, variant: 'neutral' as const }

  const submit = () => {
    try {
      const payload: IntegrationUpdatePayload = {
        display_name: form.display_name.trim(), status: form.status, base_url: form.base_url.trim(), token_url: form.token_url.trim(),
        allowed_ips: splitList(form.allowed_ips), capabilities: splitList(form.capabilities), config: configWithHolder(form),
      }
      // Blank secret fields keep what is stored.
      if (form.client_id.trim()) payload.client_id = form.client_id.trim()
      if (form.client_secret.trim()) payload.client_secret = form.client_secret.trim()
      if (form.api_key.trim()) payload.api_key = form.api_key.trim()
      if (form.webhook_secret.trim()) payload.webhook_secret = form.webhook_secret.trim()
      if (form.signing_key.trim()) payload.signing_key = form.signing_key.trim()
      save.mutate(payload)
    } catch (e) {
      toast.error((e as Error).message)
    }
  }

  return (
    <Modal open onClose={close} title={integration.display_name} size="lg">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={s.variant}>{s.label}</Badge>
          <span className="text-body-sm text-ink-muted">{bankLabel(integration.provider)}</span>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Fact label="Last check" value={integration.last_health_status || 'Not checked yet'} sub={when(integration.last_health_check)} />
          <Fact label="Updated" value={when(integration.updated_at)} />
          <Fact label="Can do" value={(integration.capabilities || []).join(', ') || '—'} />
          <Fact label="Address" value={<span className="font-mono text-caption break-all">{integration.base_url}</span>} />
          {integration.ipn_url && (
            <Fact
              label="IPN address (register in JengaHQ → Settings → IPN)"
              value={<span className="font-mono text-caption break-all">{integration.ipn_url}</span>}
            />
          )}
        </dl>

        {mode === 'view' && (
          <div className="flex flex-wrap gap-2 pt-4 border-t border-hairline">
            <Button onClick={() => test.mutate()} isLoading={test.isPending}>Test the connection</Button>
            {integration.provider === 'mpesa' && integration.status === 'active' && (
              <Button variant="secondary" onClick={() => register.mutate()} isLoading={register.isPending}>
                {integration.config?.urls_registered_at ? 'Register with Safaricom again' : 'Register with Safaricom'}
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => {
                setForm({
                  provider: integration.provider, display_name: integration.display_name, status: integration.status,
                  client_id: '', client_secret: '', api_key: '', base_url: integration.base_url, token_url: integration.token_url,
                  webhook_secret: '', signing_key: '', allowed_ips: (integration.allowed_ips || []).join(', '),
                  capabilities: (integration.capabilities || []).join(', '),
                  config: JSON.stringify(Object.fromEntries(Object.entries(integration.config || {}).filter(([k]) => !['account_holder_name', 'setup_payment_reference'].includes(k))), null, 2),
                  setup_payment_reference: String((integration.config as Record<string, unknown> | undefined)?.setup_payment_reference ?? ''),
                  account_holder_name: String((integration.config as Record<string, unknown> | undefined)?.account_holder_name ?? ''),
                })
                setMode('edit')
              }}
            >
              Edit
            </Button>
            {integration.status !== 'inactive' && <Button variant="ghost" onClick={() => setMode('off')}>Turn off</Button>}
          </div>
        )}

        {mode === 'edit' && (
          <div className="pt-4 border-t border-hairline space-y-3">
            <p className="text-caption text-ink-muted">Leave the secret fields empty to keep what's saved.</p>
            <ConnectionForm form={form} setForm={setForm} editing />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setMode('view')}>Back</Button>
              <Button onClick={submit} isLoading={save.isPending}>Save</Button>
            </div>
          </div>
        )}

        {mode === 'off' && (
          <div className="pt-4 border-t border-hairline space-y-2">
            <p className="text-body-sm text-ink">
              {integration.display_name} will stop receiving and checking payments for this customer. You can set it
              live again later by editing it.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setMode('view')}>Back</Button>
              <Button variant="secondary" onClick={() => off.mutate()} isLoading={off.isPending}>Turn off</Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

function AddConnectionModal({ open, token, onClose, onCreated }: { open: boolean; token: string; onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState<FormState>({ ...emptyForm })
  const create = useMutation({
    mutationFn: (payload: IntegrationCreatePayload) => adminService.createIntegration(payload, token),
    onSuccess: () => { toast.success('Connection added — test it before setting it live'); onCreated(); setForm({ ...emptyForm }); onClose() },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't add the connection")),
  })
  const ready = form.display_name.trim() && form.client_id.trim() && form.client_secret.trim() && form.base_url.trim() && form.token_url.trim() && form.account_holder_name.trim()

  const submit = () => {
    try {
      create.mutate({
        provider: form.provider, display_name: form.display_name.trim(), status: form.status,
        client_id: form.client_id.trim(), client_secret: form.client_secret.trim(), api_key: form.api_key.trim() || null,
        base_url: form.base_url.trim(), token_url: form.token_url.trim(), webhook_secret: form.webhook_secret.trim() || null,
        signing_key: form.signing_key.trim() || null,
        allowed_ips: splitList(form.allowed_ips), capabilities: splitList(form.capabilities), config: configWithHolder(form),
      })
    } catch (e) {
      toast.error((e as Error).message)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Add a bank connection" size="lg">
      <div className="space-y-4">
        <ConnectionForm form={form} setForm={setForm} />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!ready} isLoading={create.isPending}>Add connection</Button>
        </div>
      </div>
    </Modal>
  )
}

function ConnectionForm({ form, setForm, editing = false }: { form: FormState; setForm: Dispatch<SetStateAction<FormState>>; editing?: boolean }) {
  const set = (k: keyof FormState) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }))
  const isEquity = form.provider === 'equity'
  const isMpesa = form.provider === 'mpesa'
  const setProvider = (e: { target: { value: string } }) => {
    const provider = e.target.value
    // Equity uses Jenga, M-PESA uses Daraja: fill their sandbox addresses and settings to start from.
    const defaults = editing ? {} : provider === 'equity' ? EQUITY_SANDBOX : provider === 'mpesa' ? MPESA_SANDBOX : {}
    setForm((f) => ({ ...f, provider, ...defaults }))
  }
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="block"><span className={labelCls}>Bank</span>
          <select value={form.provider} onChange={setProvider} className={field} disabled={editing} aria-label="Bank">
            {BANKS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </label>
        <label className="block"><span className={labelCls}>Name</span>
          <input value={form.display_name} onChange={set('display_name')} placeholder="e.g. KCB collections" className={field} />
        </label>
        <label className="block"><span className={labelCls}>Status</span>
          <select value={form.status} onChange={set('status')} className={field} aria-label="Status">
            <option value="testing">Testing</option>
            <option value="active">Live</option>
            <option value="inactive">Off</option>
          </select>
        </label>
      </div>
      <label className="block">
        <span className={labelCls}>Registered to, as the bank confirmed it</span>
        <input value={form.account_holder_name} onChange={set('account_holder_name')} placeholder="As on the bank's confirmation letter" className={field} />
        <span className="block text-caption text-ink-muted mt-1">
          Ask the bank, or read it from the bank's onboarding papers. Never copy what the customer typed: this is how LipaFlow knows the account is theirs.
        </span>
      </label>
        <label className="block mt-3 text-sm">Test payment account reference (paybill only; defaults to the connected bank account number)
          <input value={form.setup_payment_reference} onChange={set('setup_payment_reference')} placeholder="Reference confirmed by the provider" className={field} />
        </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block"><span className={labelCls}>{isEquity ? 'Merchant code' : isMpesa ? 'Consumer key' : 'Client ID'}</span>
          <input value={form.client_id} onChange={set('client_id')} autoComplete="off" className={clsx(field, 'font-mono')} />
        </label>
        {/* Secrets are masked and never pre-filled. */}
        <label className="block"><span className={labelCls}>{isEquity || isMpesa ? 'Consumer secret' : 'Client secret'}</span>
          <input type="password" value={form.client_secret} onChange={set('client_secret')} autoComplete="new-password" className={clsx(field, 'font-mono')} />
        </label>
        <label className="block"><span className={labelCls}>Connection address</span>
          <input value={form.base_url} onChange={set('base_url')} placeholder="https://…" className={clsx(field, 'font-mono')} />
        </label>
        <label className="block"><span className={labelCls}>Sign-in address</span>
          <input value={form.token_url} onChange={set('token_url')} placeholder="https://…/oauth/token" className={clsx(field, 'font-mono')} />
        </label>
      </div>
      <details className="rounded-card border border-hairline">
        <summary className="cursor-pointer px-4 py-3 text-body-sm font-medium text-ink">Advanced</summary>
        <div className="px-4 pb-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block"><span className={labelCls}>{isEquity ? 'Api-Key (required)' : isMpesa ? 'Lipa na M-PESA passkey (for payment prompts)' : 'API key (optional)'}</span>
              <input type="password" value={form.api_key} onChange={set('api_key')} autoComplete="new-password" className={clsx(field, 'font-mono')} />
            </label>
            <label className="block"><span className={labelCls}>{isEquity ? 'IPN password (username goes in extra settings as ipn_username)' : isMpesa ? 'Callback token (leave blank: LipaFlow makes one)' : 'Webhook secret (needed to go live without an IP allow-list)'}</span>
              <input type="password" value={form.webhook_secret} onChange={set('webhook_secret')} autoComplete="new-password" className={clsx(field, 'font-mono')} />
            </label>
            <label className="block"><span className={labelCls}>Allowed IP ranges (comma or new line)</span>
              <textarea rows={3} value={form.allowed_ips} onChange={set('allowed_ips')} placeholder="41.90.0.0/16" className={clsx(field, 'font-mono')} />
            </label>
            <label className="block"><span className={labelCls}>What it can do (comma or new line)</span>
              <textarea rows={3} value={form.capabilities} onChange={set('capabilities')} placeholder="stk_push, c2b, account_query" className={clsx(field, 'font-mono')} />
            </label>
          </div>
          {isEquity && (
            <label className="block"><span className={labelCls}>RSA private key (PEM). Upload its public half to JengaHQ. Blank keeps the stored key.</span>
              <textarea rows={5} value={form.signing_key} onChange={set('signing_key')} placeholder="-----BEGIN PRIVATE KEY-----" autoComplete="off" spellCheck={false} className={clsx(field, 'font-mono')} />
            </label>
          )}
          <label className="block"><span className={labelCls}>Extra settings (JSON)</span>
            <textarea rows={5} value={form.config} onChange={set('config')} className={clsx(field, 'font-mono')} />
          </label>
        </div>
      </details>
    </div>
  )
}

function Fact({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div>
      <dt className="text-caption text-ink-faint">{label}</dt>
      <dd className="text-body-sm text-ink mt-0.5">{value}</dd>
      {sub && <dd className="text-caption text-ink-muted">{sub}</dd>}
    </div>
  )
}
